import React from 'react';
import { FiTrendingUp } from 'react-icons/fi';

// Soft icon-chip tints. The legacy `gradient` prop ("from-blue-500 to-...")
// is still accepted — its first color picks the chip tint.
const CHIP_TINTS = {
  blue: 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400',
  cyan: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-500/10 dark:text-cyan-400',
  teal: 'bg-teal-50 text-teal-600 dark:bg-teal-500/10 dark:text-teal-400',
  green: 'bg-green-50 text-green-600 dark:bg-green-500/10 dark:text-green-400',
  emerald: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400',
  yellow: 'bg-yellow-50 text-yellow-600 dark:bg-yellow-500/10 dark:text-yellow-400',
  amber: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400',
  orange: 'bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400',
  red: 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400',
  pink: 'bg-pink-50 text-pink-600 dark:bg-pink-500/10 dark:text-pink-400',
  purple: 'bg-purple-50 text-purple-600 dark:bg-purple-500/10 dark:text-purple-400',
  indigo: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400',
  accent: 'bg-accent-soft text-accent',
};

const tintFromGradient = (gradient = '') => {
  const match = gradient.match(/from-([a-z]+)-/);
  return CHIP_TINTS[match?.[1]] || CHIP_TINTS.accent;
};

export default function StatCard({
  title,
  value,
  icon: Icon,
  gradient,
  tone,
  trend,
  trendValue,
  onClick,
  className = '',
}) {
  const Comp = onClick ? 'button' : 'div';
  const chip = tone ? CHIP_TINTS[tone] || CHIP_TINTS.accent : tintFromGradient(gradient);
  return (
    <Comp
      onClick={onClick}
      className={`w-full text-start bg-surface rounded-xl border border-edge p-5 transition-all duration-200 hover:shadow-md hover:border-edge-strong ${
        onClick ? 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring' : ''
      } ${className}`}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium uppercase tracking-wide text-content-muted truncate min-w-0">{title}</p>
        {Icon && (
          <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${chip}`}>
            <Icon size={18} />
          </span>
        )}
      </div>
      <div className="mt-2 flex items-end justify-between gap-2 min-w-0">
        {/* Responsive value: scales down on dense layouts and wraps rather than
            clipping when the number is large (e.g. money totals + currency mark). */}
        <h3 className="text-xl sm:text-2xl font-bold text-content tabular-nums leading-tight min-w-0 break-words">{value}</h3>
        {trend && (
          <span
            className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded-md mb-0.5 ${
              trend === 'up'
                ? 'text-green-700 bg-green-50 dark:text-green-400 dark:bg-green-500/10'
                : 'text-red-700 bg-red-50 dark:text-red-400 dark:bg-red-500/10'
            }`}
          >
            <FiTrendingUp size={12} className={trend === 'down' ? 'rotate-180' : ''} />
            {trendValue}
          </span>
        )}
      </div>
    </Comp>
  );
}
